import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";

interface AuditLogDetailsProps {
  log: {
    id: string;
    table_name: string;
    record_id: string;
    action: string;
    old_data: any;
    new_data: any;
    changed_fields: string[];
    changed_by: string | null;
    changed_by_name?: string;
    changed_by_email?: string;
    changed_at: string;
  };
}

export function AuditLogDetails({ log }: AuditLogDetailsProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (value.length === 0) return "Empty";
        return value.join(", ");
      }
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === "string" && value.length > 100) {
      return value.substring(0, 100) + "...";
    }
    return String(value);
  };

  const renderFieldComparison = (field: string) => {
    const oldValue = log.old_data?.[field];
    const newValue = log.new_data?.[field];

    if (log.action === "INSERT") {
      return (
        <div key={field} className="p-3 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="font-semibold text-sm text-foreground">{field}:</span>
            <Badge variant="default" className="bg-green-600 text-white">New</Badge>
          </div>
          <div className="text-sm text-green-700 dark:text-green-300 font-mono whitespace-pre-wrap break-all">
            {formatValue(newValue)}
          </div>
        </div>
      );
    }

    if (log.action === "DELETE") {
      return (
        <div key={field} className="p-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="font-semibold text-sm text-foreground">{field}:</span>
            <Badge variant="destructive">Deleted</Badge>
          </div>
          <div className="text-sm text-red-700 dark:text-red-300 font-mono whitespace-pre-wrap break-all line-through">
            {formatValue(oldValue)}
          </div>
        </div>
      );
    }

    // UPDATE action
    return (
      <div key={field} className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800">
        <div className="flex items-start justify-between gap-2 mb-2">
          <span className="font-semibold text-sm text-foreground">{field}:</span>
          <Badge variant="secondary" className="bg-yellow-600 text-white">Modified</Badge>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1 font-semibold">Old Value:</div>
            <div className="text-sm text-red-700 dark:text-red-400 font-mono whitespace-pre-wrap break-all p-2 bg-red-100 dark:bg-red-900/30 rounded border border-red-300 dark:border-red-700">
              {formatValue(oldValue)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 font-semibold">New Value:</div>
            <div className="text-sm text-green-700 dark:text-green-400 font-mono whitespace-pre-wrap break-all p-2 bg-green-100 dark:bg-green-900/30 rounded border border-green-300 dark:border-green-700">
              {formatValue(newValue)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4 bg-muted/30 rounded-lg border">
      {/* Header Info */}
      <div className="flex flex-wrap gap-4 pb-4 border-b">
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs text-muted-foreground mb-1">Record ID</div>
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono bg-muted px-2 py-1 rounded">{log.record_id}</code>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={() => copyToClipboard(log.record_id, "record_id")}
            >
              {copiedField === "record_id" ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs text-muted-foreground mb-1">Changed By</div>
          <div className="text-sm">
            {log.changed_by_name ? (
              <div>
                <div className="font-medium">{log.changed_by_name}</div>
                <div className="text-xs text-muted-foreground">{log.changed_by_email}</div>
              </div>
            ) : (
              <span className="text-muted-foreground">System</span>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs text-muted-foreground mb-1">Timestamp</div>
          <div className="text-sm font-medium">
            {format(new Date(log.changed_at), "PPpp")}
          </div>
        </div>
      </div>

      {/* Changed Fields */}
      <div>
        <h4 className="font-semibold mb-3 text-sm">
          Changed Fields ({log.changed_fields?.length || 0})
        </h4>
        <div className="space-y-3">
          {log.changed_fields && log.changed_fields.length > 0 ? (
            log.changed_fields.map((field) => renderFieldComparison(field))
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              No field changes recorded
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
